// PedidoIndividual.js - Panel lateral para crear cotización
import React, { useState, useEffect, useMemo } from 'react';
import { X, ShoppingCart, User, Search, Plus, Save, Loader2, CheckCircle, Percent, Trash2 } from 'lucide-react';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
const formatBs = (num) => new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(num || 0);

const SelectFilter = ({ label, icon: Icon, options = [], value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const selectedLabel = value
    ? (options.find(opt => (opt.value || opt) === value)?.label || value)
    : "Seleccione...";

  return (
    <div className="filter-pill-container2" ref={dropdownRef}>
      <button className={`filter-pill-button2 ${value ? 'active' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }} type="button" disabled={disabled}>
        {Icon && <Icon size={12} className="pill-icon" />}
        <span className="pill-label2">{label}:</span>
        <span className="pill-value2">{selectedLabel}</span>
      </button>
      {isOpen && (
        <div className="filter-dropdown-panel2">
          {showSearch && (
            <div className="search-wrapper2">
              <Search size={14} /><input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const val = opt.value || opt;
              return (
                <div key={`${label}-${i}`} className={`checkbox-row2 ${val === value ? 'selected' : ''}`}
                  onClick={() => { onChange(val); setIsOpen(false); setSearchTerm(""); }}>
                  <span>{opt.label || opt}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PedidoIndividual = ({ catalogData, isAsesor, username, onClose, onOrderCreated }) => {
  const { getProductosCliente, crearCotizacion } = useQuery();
  const { profile } = useAuth();

  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientInfo, setSelectedClientInfo] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [currentLine, setCurrentLine] = useState({ marca: '' });
  const [productosCliente, setProductosCliente] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [successModal, setSuccessModal] = useState({ show: false, message: '', factNum: '' });
  const [bulkSelection, setBulkSelection] = useState({});
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [ordenCompra, setOrdenCompra] = useState('OC - ');

  // Clientes
  const clientesOptions = useMemo(() => {
    if (!catalogData?.clientes) return [];
    let clients = catalogData.clientes;
    if (isAsesor && username) {
      clients = clients.filter(c => c.username_vendedor === username || c.codigo_vendedor === profile?.codigo_vendedor);
    }
    return clients.map(c => ({ value: c.id_cliente, label: `${c.id_cliente} - ${c.cliente_principal} - ${c.cliente_nombre}` }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [catalogData, isAsesor, username, profile]);

  const availableMarcas = useMemo(() => {
    if (productosCliente.length > 0) return [...new Set(productosCliente.map(p => p.marca))].sort();
    return catalogData?.marcas || [];
  }, [productosCliente, catalogData]);

  useEffect(() => {
    if (!selectedClientId) { setProductosCliente([]); setSelectedClientInfo(null); return; }
    const clientData = catalogData?.clientes?.find(c => c.id_cliente === selectedClientId);
    setSelectedClientInfo(clientData || null);
    const fetchProductos = async () => {
      setLoadingProductos(true);
      try { const data = await getProductosCliente(selectedClientId); if (data?.success) setProductosCliente(data.productos); }
      catch { setProductosCliente([]); }
      finally { setLoadingProductos(false); }
    };
    fetchProductos();
  }, [selectedClientId, getProductosCliente, catalogData]);

  const totalBs = useMemo(() => orderLines.reduce((acc, l) => acc + l.bultos * (l.precio_bs || 0), 0), [orderLines]);
  const totalUSD = useMemo(() => orderLines.reduce((acc, l) => acc + l.bultos * (l.precio_usd || 0), 0), [orderLines]);

  const handleSaveOrder = async () => {
    if (!selectedClientId || orderLines.length === 0) return;
    setSavingOrder(true);
    try {
      const codigoVendedor = productosCliente[0]?.codigo_vendedor || profile?.codigo_vendedor || '01';
      const payload = {
        id_cliente: selectedClientId, moneda_pago: 'USD', codigo_vendedor: codigoVendedor,
        lineas: orderLines.map(line => ({
          sku: line.sku, marca: line.marca, nombre_producto: line.nombre_producto,
          bultos: line.bultos, precio_bs: line.precio_bs, precio_bs2: line.precio_bs2,
          precio_usd: line.precio_usd, uni_venta: line.uni_venta, tipo_imp: line.tipo_imp,
          equivalencia: line.equivalencia, cos_pro_un: line.cos_pro_un, ult_cos_un: line.ult_cos_un,
          cos_pro_om: line.cos_pro_om || 0, ult_cos_om: line.ult_cos_om || 0,
          descuento: line.descuento || 0, descuento_pac: line.descuento_pac || 0, descuento_final: 0,
        })),
        total_bs: totalBs, total_usd: totalUSD, usuario: username || 'SYSTEM',
        orden_compra: ordenCompra
      };
      const result = await crearCotizacion(payload);
      if (!result.success) throw new Error(result.detail || 'Error');
      setSuccessModal({ show: true, message: `Cotización N° ${result.fact_num} creada.`, factNum: result.fact_num });
      if (onOrderCreated) onOrderCreated();
    } catch (error) { alert(`Error: ${error.response?.data?.detail || error.message}`); }
    finally { setSavingOrder(false); }
  };

  const limpiarFormulario = () => {
    setSelectedClientId(''); setOrderLines([]); setProductosCliente([]);
    setSelectedClientInfo(null); setBulkSelection({}); setBulkSearchTerm('');
  };

  if (successModal.show) {
    return (
      <>
        <div className="order-panel-overlay" style={{ zIndex: 1001 }} onClick={() => { setSuccessModal({ show: false }); limpiarFormulario(); onClose(); }} />
        <div className="success-modal" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(15,20,35,0.98)', backdropFilter: 'blur(20px)', borderRadius: '20px', padding: '2rem', textAlign: 'center', zIndex: 1002, boxShadow: '0 10px 30px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)', width: '90%', maxWidth: '400px', fontFamily: 'Montserrat, sans-serif' }}>
          <CheckCircle size={48} style={{ color: '#00ba52', marginBottom: '1rem' }} />
          <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>{successModal.message}</p>
          <button onClick={() => { setSuccessModal({ show: false }); limpiarFormulario(); onClose(); }}
            style={{ marginTop: '1.5rem', background: '#00ba52', color: '#000', border: 'none', padding: '0.7rem 2rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>Aceptar</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="order-panel-overlay" onClick={onClose} />
      <div className="order-panel slide-in-right">
        <div className="order-panel-header">
          <h2 className="order-panel-title"><ShoppingCart size={20} /><span>Solicitud de Cotización</span></h2>
          <button className="close-panel-btn" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="order-panel-body">
          <div className="order-section">
            <h3 className="section-subtitle">Seleccione Cliente {isAsesor && "(su cartera)"}</h3>
            <SelectFilter label="Cliente" icon={User} options={clientesOptions} value={selectedClientId}
              onChange={(val) => { setSelectedClientId(val); setProductosCliente([]); setOrderLines([]); setBulkSelection({}); setBulkSearchTerm(''); }} />
            {loadingProductos && <p style={{ color: '#ccc', fontSize: '0.8rem', marginTop: '0.5rem' }}>Cargando productos...</p>}
            {selectedClientInfo && (
              <div className="client-info-extra" style={{ marginTop: '0.7rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '0.6rem', fontSize: '0.75rem', color: '#aaa' }}>
                <div><strong>RIF:</strong> {selectedClientInfo.rif || 'N/A'}</div>
                <div><strong>Desc. Global:</strong> {selectedClientInfo.porc_desc || 'N/A'}</div>
                <div><strong>Contribuyente:</strong> {selectedClientInfo.contrib ? 'Sí' : 'No'}</div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <strong>Orden de Compra:</strong>
                  <input
                    type="text"
                    value={ordenCompra}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val.startsWith('OC - ')) {
                        setOrdenCompra('OC - ' + val.replace(/^OC - /, ''));
                      } else {
                        setOrdenCompra(val);
                      }
                    }}
                    placeholder="OC - Número"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '6px',
                      padding: '0.4rem 0.6rem',
                      color: '#fff',
                      fontSize: '0.8rem',
                      outline: 'none',
                      width: '100%'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="order-section" style={{ textAlign: 'center' }}>
            <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>Moneda: <strong style={{ color: '#00ba52' }}>USD</strong></p>
          </div>
          {selectedClientId && !loadingProductos && (
            <div className="order-section add-line-section">
              <h3 className="section-subtitle">Agregar productos</h3>
              <SelectFilter label="Marca" options={availableMarcas.map(m => ({ value: m, label: m }))} value={currentLine.marca}
                onChange={(val) => { setCurrentLine({ marca: val }); setBulkSelection({}); setBulkSearchTerm(''); }} />
              {currentLine.marca && (
                <div className="bulk-product-table" style={{ marginTop: '1rem' }}>
                  <div style={{ marginBottom: '0.8rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
                      <Search size={14} style={{ color: 'rgba(255,255,255,0.5)', marginRight: '8px' }} />
                      <input type="text" placeholder="Buscar por SKU o nombre..." value={bulkSearchTerm}
                        onChange={(e) => setBulkSearchTerm(e.target.value)}
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: '0.8rem', fontFamily: 'Montserrat, sans-serif' }} />
                    </div>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {productosCliente.filter(p => p.marca === currentLine.marca).filter(p => {
                      if (!bulkSearchTerm) return true;
                      const t = bulkSearchTerm.toLowerCase();
                      return (p.sku && p.sku.toLowerCase().includes(t)) || (p.nombre && p.nombre.toLowerCase().includes(t));
                    }).map(prod => {
                      const precioUnitario = prod.precio_usd;
                      const sel = bulkSelection[prod.sku] || { checked: false, cantidad: 0 };
                      const subtotal = sel.cantidad * precioUnitario;
                      return (
                        <div key={prod.sku} className="bulk-row" style={{ display: 'flex', alignItems: 'center', padding: '0.4rem', borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'white' }}>
                          <span style={{ flex: 0.5 }}><input type="checkbox" checked={sel.checked} onChange={(e) => { const c = e.target.checked; setBulkSelection(prev => ({ ...prev, [prod.sku]: { checked: c, cantidad: c ? (prev[prod.sku]?.cantidad || 1) : 0 } })); }} /></span>
                          <span style={{ flex: 2, fontSize: '0.8rem' }}><div style={{ fontWeight: 500 }}>{prod.sku}</div><div style={{ color: '#aaa', fontSize: '0.7rem' }}>{prod.nombre}</div></span>
                          <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '8px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '2px' }}>{formatUSD(precioUnitario)}<span style={{ fontSize: '0.65rem', marginLeft: '4px' }}>{prod.uni_venta}</span></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input type="number" min="0" disabled={!sel.checked} style={{ width: '55px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '0.2rem 0.3rem', color: '#fff', textAlign: 'center', fontSize: '0.8rem' }} value={sel.cantidad} onChange={(e) => { const c = Math.max(0, parseInt(e.target.value) || 0); setBulkSelection(prev => ({ ...prev, [prod.sku]: { ...prev[prod.sku], cantidad: c } })); }} />
                              <span style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-theme)', minWidth: '60px', textAlign: 'right' }}>{formatUSD(subtotal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                    <button onClick={() => {
                      const nuevas = Object.entries(bulkSelection).filter(([_, s]) => s.checked && s.cantidad > 0).map(([sku, s]) => {
                        const p = productosCliente.find(x => x.sku === sku);
                        return { marca: currentLine.marca, sku, bultos: s.cantidad, precio_bs: p.precio_bs || 0, precio_bs2: p.precio_bs2 || 0, precio_usd: p.precio_usd || 0, nombre_producto: p.nombre || '', uni_venta: p.uni_venta || 'UND', tipo_imp: p.tipo_imp || 1, equivalencia: p.equivalencia || 1.0, cos_pro_un: p.cos_pro_un || 0, ult_cos_un: p.ult_cos_un || 0, cos_pro_om: 0, ult_cos_om: 0, descuento: 0, descuento_pac: 0, descuento_final: 0 };
                      });
                      setOrderLines(prev => { const m = new Map(prev.map(l => [l.sku, l])); nuevas.forEach(n => { if (m.has(n.sku)) { const e = m.get(n.sku); m.set(n.sku, { ...e, bultos: e.bultos + n.bultos }); } else { m.set(n.sku, n); } }); return Array.from(m.values()); });
                      setBulkSelection({});
                    }} disabled={Object.values(bulkSelection).every(s => !s.checked || s.cantidad === 0)} className="save-order-btn">
                      <Plus size={18} /> Agregar seleccionados
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="order-section">
            <h3 className="section-subtitle">Productos en la cotización</h3>
            {orderLines.length === 0 ? <p className="empty-hint">Agregue productos.</p> : (
              <div className="order-lines-list">
                {orderLines.map((line, idx) => (
                  <div key={idx} className="order-line-card">
                    <div className="line-details" style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span className="line-marca">{line.marca}</span><span className="line-separator">•</span>
                        <span className="line-sku">{line.sku}</span><span className="line-bultos">{line.bultos} {line.uni_venta}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#ccc' }}>{line.nombre_producto || '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                        <span className="line-precio">{formatUSD(line.bultos * (line.precio_usd || 0))}</span>
                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '2px 6px', gap: '3px' }}>
                          <Percent size={11} style={{ color: '#a78bfa' }} /><span style={{ fontSize: '0.65rem', color: '#a78bfa' }}>Prop.</span>
                          <input type="number" min="0" max="100" step="0.01" value={line.descuento || 0}
                            onChange={(e) => { const nl = [...orderLines]; nl[idx] = { ...nl[idx], descuento: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) }; setOrderLines(nl); }}
                            style={{ width: '44px', background: 'transparent', border: 'none', outline: 'none', color: '#fff', textAlign: 'center', fontSize: '0.78rem', fontFamily: 'Montserrat, sans-serif' }} />
                          <span style={{ color: '#aaa', fontSize: '0.68rem' }}>%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(34,197,94,0.08)', borderRadius: '6px', padding: '2px 6px', gap: '3px', border: '1px solid rgba(34,197,94,0.2)' }}>
                          <Percent size={11} style={{ color: '#4ade80' }} /><span style={{ fontSize: '0.65rem', color: '#4ade80' }}>PAC</span>
                          <input type="number" min="0" max="100" step="0.01" value={line.descuento_pac || 0}
                            onChange={(e) => { const nl = [...orderLines]; nl[idx] = { ...nl[idx], descuento_pac: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) }; setOrderLines(nl); }}
                            style={{ width: '44px', background: 'transparent', border: 'none', outline: 'none', color: '#fff', textAlign: 'center', fontSize: '0.78rem', fontFamily: 'Montserrat, sans-serif' }} />
                          <span style={{ color: '#aaa', fontSize: '0.68rem' }}>%</span>
                        </div>
                      </div>
                    </div>
                    <button className="line-remove-btn" onClick={() => setOrderLines(prev => prev.filter((_, i) => i !== idx))}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
            {orderLines.length > 0 && (
              <div className="order-totals" style={{ marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '0.8rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#aaa' }}>Total USD:</span><strong>{formatUSD(totalUSD)}</strong></div>
                <p style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: '4px' }}>Equivale a: {formatBs(totalBs)}</p>
              </div>
            )}
          </div>
          <div className="order-panel-footer">
            <button className="save-order-btn" onClick={handleSaveOrder} disabled={orderLines.length === 0 || !selectedClientId || savingOrder}>
              {savingOrder ? <Loader2 size={15} className="spin-anim" /> : <Save size={15} />}
              <span>{savingOrder ? 'Guardando...' : 'Guardar Cotización'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PedidoIndividual;